import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/Button';
import { SectionHeader } from '../components/SectionHeader';
import { ServiceCard } from '../components/ServiceCard';
import { AnimatedCounter } from '../components/AnimatedCounter';
import { TestimonialsSlider } from '../components/TestimonialsSlider';
import { FAQAccordion } from '../components/FAQAccordion';
import { GlowOrb } from '../components/GlowOrb';
import { services } from '../data/services';
import { blogPosts, faqs } from '../data/content';
import { ArrowRight, Sparkles, Heart, Shield, Sun, Moon, Star, Activity } from 'lucide-react';
import { Link } from 'react-router-dom';
import { images, blogImageMap } from '../data/images';
export function Home() {
  const featuredServices = services.slice(0, 4);
  const chakras = [{
    name: 'Crown',
    sanskrit: 'Sahasrara',
    color: 'bg-purple-400',
    desc: 'Spiritual connection & divine wisdom'
  }, {
    name: 'Third Eye',
    sanskrit: 'Ajna',
    color: 'bg-indigo-400',
    desc: 'Intuition & inner vision'
  }, {
    name: 'Throat',
    sanskrit: 'Vishuddha',
    color: 'bg-blue-400',
    desc: 'Communication & authentic expression'
  }, {
    name: 'Heart',
    sanskrit: 'Anahata',
    color: 'bg-green-400',
    desc: 'Love, compassion & emotional balance'
  }, {
    name: 'Solar Plexus',
    sanskrit: 'Manipura',
    color: 'bg-yellow-400',
    desc: 'Personal power & confidence'
  }, {
    name: 'Sacral',
    sanskrit: 'Svadhisthana',
    color: 'bg-orange-400',
    desc: 'Creativity & emotional fluidity'
  }, {
    name: 'Root',
    sanskrit: 'Muladhara',
    color: 'bg-red-500',
    desc: 'Grounding, safety & physical vitality'
  }];
  return <div className="overflow-hidden" data-id="element-550">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 pb-32" data-id="element-551">
        {/* Cinematic Background Image */}
        <div className="absolute inset-0 overflow-hidden" data-id="element-552">
          <div className="absolute inset-0 bg-cover bg-center opacity-40" style={{
          backgroundImage: `url(${images.cosmicNebula})`
        }} data-id="element-553"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-midnight/80 via-midnight/60 to-midnight" data-id="element-554"></div>
        </div>
        <GlowOrb color="primary" size="xl" position="-top-40 -left-40" delay={0} data-id="element-555" />
        <GlowOrb color="gold" size="lg" position="top-1/4 right-0" delay={2} data-id="element-556" />
        <GlowOrb color="blue" size="md" position="bottom-0 left-1/3" delay={4} data-id="element-557" />
        <div className="absolute inset-0 bg-star-dust opacity-50 mix-blend-screen pointer-events-none" data-id="element-558"></div>

        <div className="container mx-auto px-6 relative z-10" data-id="element-559">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          ease: 'easeOut'
        }} className="max-w-5xl mx-auto text-center" data-id="element-560">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-md mb-8" data-id="element-561">
              <Sparkles className="w-4 h-4 text-gold" data-id="element-562" />
              <span className="text-xs font-bold tracking-[0.2em] uppercase text-beige" data-id="element-563">
                Sacred Healing Sanctuary
              </span>
              <Sparkles className="w-4 h-4 text-gold" data-id="element-564" />
            </div>

            <h1 className="text-5xl md:text-7xl lg:text-[5.5rem] font-serif text-white mb-8 leading-[1.1] tracking-tight" data-id="element-565">
              Awaken Your Inner Energy & <br className="hidden md:block" data-id="element-566" />
              <span className="text-cosmic-gradient italic pr-2" data-id="element-567">
                Transform
              </span>{' '}
              Your Life
            </h1>

            <p className="text-lg md:text-xl text-beige-muted mb-12 max-w-2xl mx-auto text-balance font-light" data-id="element-568">
              Discover deep spiritual healing through chakra balancing, aura
              cleansing, and personalized astrology guidance designed to restore
              clarity, peace, and purpose.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-6" data-id="element-569">
              <Button href="/booking" size="lg" variant="primary" data-id="element-570">
                Begin Your Journey
              </Button>
              <Button href="/services" variant="glass" size="lg" data-id="element-571">
                Explore Services
              </Button>
            </div>
          </motion.div>

          {/* Stats Glass Strip */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="max-w-4xl mx-auto mt-24" data-id="element-572">
            <div className="glass rounded-3xl p-8 grid grid-cols-2 md:grid-cols-4 gap-8 divide-x divide-white/10" data-id="element-573">
              {[{
              label: 'Souls Healed',
              value: 5000,
              suffix: '+'
            }, {
              label: 'Years Experience',
              value: 12,
              suffix: ''
            }, {
              label: 'Countries Served',
              value: 50,
              suffix: '+'
            }, {
              label: 'Average Rating',
              value: 4.9,
              suffix: '★'
            }].map((stat, i) => <div key={i} className="text-center px-4 first:border-l-0 border-white/10" data-id="element-574">
                  <div className="text-3xl md:text-4xl font-serif text-white mb-2" data-id="element-575">
                    <AnimatedCounter value={stat.value} suffix={stat.suffix} data-id="element-576" />
                  </div>
                  <div className="text-[10px] text-gold uppercase tracking-widest font-bold" data-id="element-577">
                    {stat.label}
                  </div>
                </div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-32 relative" data-id="element-578">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" data-id="element-579"></div>
        <div className="container mx-auto px-6" data-id="element-580">
          <SectionHeader eyebrow="Sacred Offerings" title="Explore Our Healing Services" description="Personalized spiritual healing designed for your unique journey. Discover the path that resonates with your soul." data-id="element-581" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16" data-id="element-582">
            {featuredServices.map((service, index) => <motion.div key={service.id} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1,
            duration: 0.6
          }} data-id="element-583">
                <ServiceCard service={service} data-id="element-584" />
              </motion.div>)}
          </div>

          <div className="text-center" data-id="element-585">
            <Button href="/services" variant="outline" icon={<ArrowRight className="w-4 h-4" data-id="element-587" />} data-id="element-586">
              View All 22 Services
            </Button>
          </div>
        </div>
      </section>

      {/* Journey Section */}
      <section className="py-32 bg-midnight-panel relative overflow-hidden" data-id="element-588">
        <div className="absolute inset-0 bg-cover bg-center opacity-15" style={{
        backgroundImage: `url(${images.candleFlame})`
      }} data-id="element-589"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-midnight-panel via-midnight-panel/80 to-midnight-panel" data-id="element-590"></div>
        <GlowOrb color="gold" size="lg" position="top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-591" />
        <div className="container mx-auto px-6 relative z-10" data-id="element-592">
          <SectionHeader eyebrow="Your Path to Transformation" title="Sacred Healing Journey" data-id="element-593" />

          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mt-20 relative" data-id="element-594">
            {/* Desktop connecting line */}
            <div className="hidden md:block absolute top-12 left-[12.5%] right-[12.5%] h-[1px] bg-gradient-to-r from-transparent via-gold/50 to-transparent" data-id="element-595"></div>

            {[{
            icon: Sparkles,
            title: 'Discover',
            desc: 'Energy assessment & consultation'
          }, {
            icon: Heart,
            title: 'Align',
            desc: 'Personalized healing session'
          }, {
            icon: Shield,
            title: 'Integrate',
            desc: 'Guidance & daily rituals'
          }, {
            icon: Sun,
            title: 'Flourish',
            desc: 'Ongoing support & growth'
          }].map((step, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.15
          }} className="text-center relative group" data-id="element-596">
                <div className="w-24 h-24 mx-auto rounded-full glass flex items-center justify-center mb-8 relative z-10 group-hover:border-gold/50 transition-colors duration-500" data-id="element-597">
                  <div className="absolute inset-0 bg-gold/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-598"></div>
                  <step.icon className="w-8 h-8 text-gold relative z-10" data-id="element-599" />
                </div>
                <h3 className="text-2xl font-serif mb-3 text-white" data-id="element-600">
                  {step.title}
                </h3>
                <p className="text-beige-muted" data-id="element-601">{step.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 7 Chakras Section */}
      <section className="py-32 relative" data-id="element-602">
        <div className="container mx-auto px-6" data-id="element-603">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-604">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-605">
              <SectionHeader eyebrow="Ancient Wisdom" title="The Seven Sacred Energy Centers" description="Your chakras are spinning wheels of energy that govern your physical, emotional, and spiritual well-being. When balanced, they create harmony throughout your entire being." align="left" data-id="element-606" />

              <div className="space-y-6 mt-12" data-id="element-607">
                {chakras.map((chakra, i) => <motion.div key={i} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: i * 0.1
              }} className="flex items-center gap-6 group" data-id="element-608">
                    <div className={`w-4 h-4 rounded-full ${chakra.color} shadow-[0_0_15px_currentColor] opacity-70 group-hover:opacity-100 transition-opacity`} data-id="element-609"></div>
                    <div data-id="element-610">
                      <h4 className="font-serif text-xl text-white flex items-baseline gap-3" data-id="element-611">
                        {chakra.name}
                        <span className="text-xs font-sans text-gold uppercase tracking-widest" data-id="element-612">
                          {chakra.sanskrit}
                        </span>
                      </h4>
                      <p className="text-sm text-beige-muted" data-id="element-613">{chakra.desc}</p>
                    </div>
                  </motion.div>)}
              </div>
              <div className="mt-12" data-id="element-614">
                <Button href="/services/chakra-balancing" variant="outline" data-id="element-615">
                  Explore Chakra Healing
                </Button>
              </div>
            </motion.div>

            {/* Abstract Chakra Visual */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} className="relative h-[600px] flex items-center justify-center rounded-[3rem] overflow-hidden" data-id="element-616">
              <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: `url(${images.meditationWoman})`
            }} data-id="element-617"></div>
              <div className="absolute inset-0 bg-gradient-to-r from-midnight via-midnight/70 to-midnight/30" data-id="element-618"></div>
              <div className="absolute inset-0 bg-gradient-to-b from-midnight/40 via-transparent to-midnight/80" data-id="element-619"></div>
              <div className="absolute inset-0 border border-white/10 rounded-[3rem]" data-id="element-620"></div>

              <div className="relative h-[500px] flex flex-col justify-between items-center z-10" data-id="element-621">
                {chakras.map((chakra, i) => <motion.div key={i} animate={{
                scale: [1, 1.3, 1],
                opacity: [0.6, 1, 0.6]
              }} transition={{
                duration: 3,
                repeat: Infinity,
                delay: i * 0.4
              }} className="relative" data-id="element-622">
                    <div className={`absolute inset-0 ${chakra.color} blur-xl scale-150`} data-id="element-623"></div>
                    <div className={`w-10 h-10 rounded-full ${chakra.color} relative shadow-[0_0_30px_currentColor]`} data-id="element-624"></div>
                  </motion.div>)}
                {/* Connecting energy line */}
                <div className="absolute top-4 bottom-4 w-px bg-gradient-to-b from-purple-400 via-green-400 to-red-500 opacity-50 -z-10" data-id="element-625"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Calmoras (Bento Grid) */}
      <section className="py-32 bg-midnight-panel relative" data-id="element-626">
        <div className="container mx-auto px-6" data-id="element-627">
          <SectionHeader eyebrow="The Calmoras Difference" title="Why Choose Calmoras" data-id="element-628" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto" data-id="element-629">
            <div className="md:col-span-2 glass p-8 rounded-3xl relative overflow-hidden group" data-id="element-630">
              <div className="absolute top-0 right-0 w-64 h-64 bg-gold/10 rounded-full blur-3xl group-hover:bg-gold/20 transition-colors" data-id="element-631"></div>
              <Star className="w-10 h-10 text-gold mb-6 relative z-10" data-id="element-632" />
              <h3 className="text-2xl font-serif text-white mb-3 relative z-10" data-id="element-633">
                Professional Spiritual Guidance
              </h3>
              <p className="text-beige-muted relative z-10" data-id="element-634">
                Tailored to your personal energy needs with over a decade of
                proven experience in holistic healing modalities.
              </p>
            </div>

            <div className="glass p-8 rounded-3xl group" data-id="element-635">
              <Shield className="w-10 h-10 text-primary-light mb-6" data-id="element-636" />
              <h3 className="text-xl font-serif text-white mb-3" data-id="element-637">Safe Space</h3>
              <p className="text-sm text-beige-muted" data-id="element-638">
                Compassionate and 100% confidential support.
              </p>
            </div>

            <div className="glass p-8 rounded-3xl group" data-id="element-639">
              <Activity className="w-10 h-10 text-primary-light mb-6" data-id="element-640" />
              <h3 className="text-xl font-serif text-white mb-3" data-id="element-641">
                Powerful Shifts
              </h3>
              <p className="text-sm text-beige-muted" data-id="element-642">
                Real energetic transformation you can feel.
              </p>
            </div>

            <div className="md:col-span-2 glass p-8 rounded-3xl relative overflow-hidden group" data-id="element-643">
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-primary-glow/10 rounded-full blur-3xl group-hover:bg-primary-glow/20 transition-colors" data-id="element-644"></div>
              <Moon className="w-10 h-10 text-gold mb-6 relative z-10" data-id="element-645" />
              <h3 className="text-2xl font-serif text-white mb-3 relative z-10" data-id="element-646">
                Ancient Wisdom Meets Modern Practice
              </h3>
              <p className="text-beige-muted relative z-10" data-id="element-647">
                We bridge the gap between esoteric traditions and practical,
                everyday wellness for the modern soul.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Meet Aurora */}
      <section className="py-32 relative" data-id="element-648">
        <div className="container mx-auto px-6" data-id="element-649">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center max-w-6xl mx-auto" data-id="element-650">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="relative" data-id="element-651">
              <div className="aspect-[3/4] rounded-[2rem] overflow-hidden border border-gold/20 relative shadow-2xl shadow-primary/30" data-id="element-652">
                <div className="absolute inset-0 bg-cover bg-center" style={{
                backgroundImage: `url(${images.auroraPortrait})`
              }} data-id="element-653"></div>
                <div className="absolute inset-0 bg-gradient-to-t from-midnight via-midnight/20 to-transparent" data-id="element-654"></div>
                {/* Decorative corner ornament */}
                <div className="absolute top-6 left-6 w-12 h-12 border-l border-t border-gold/50" data-id="element-655"></div>
                <div className="absolute bottom-6 right-6 w-12 h-12 border-r border-b border-gold/50" data-id="element-656"></div>
              </div>
              <div className="absolute -bottom-10 -right-10 w-64 h-64 bg-gold/20 rounded-full blur-3xl -z-10" data-id="element-657"></div>
              <div className="absolute -top-10 -left-10 w-48 h-48 bg-primary-glow/20 rounded-full blur-3xl -z-10" data-id="element-658"></div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-659">
              <SectionHeader eyebrow="Your Spiritual Guide" title="Meet Aurora Bellweather" align="left" data-id="element-660" />
              <div className="space-y-6 text-beige-muted text-lg font-light leading-relaxed" data-id="element-661">
                <p data-id="element-662">
                  With over 12 years of experience in energy healing and
                  spiritual guidance, Aurora has helped thousands of souls find
                  their path to inner peace and transformation.
                </p>
                <p data-id="element-663">
                  Certified in Reiki, Astrology, Crystal Healing, and Sound
                  Therapy, she brings a holistic approach to every session,
                  combining ancient wisdom with modern understanding.
                </p>
              </div>

              <blockquote className="mt-8 border-l-2 border-gold pl-6 py-2" data-id="element-664">
                <p className="font-serif text-2xl text-white italic" data-id="element-665">
                  "My mission is to help you remember the light that has always
                  been within you."
                </p>
              </blockquote>

              <div className="mt-12" data-id="element-666">
                <Button href="/about" variant="ghost" icon={<ArrowRight className="w-4 h-4" data-id="element-668" />} data-id="element-667">
                  Read My Full Story
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Cosmic Energy */}
      <section className="py-32 bg-midnight-panel relative overflow-hidden" data-id="element-669">
        <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{
        backgroundImage: `url(${images.starfield})`
      }} data-id="element-670"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-midnight-panel via-midnight-panel/80 to-midnight-panel" data-id="element-671"></div>
        <GlowOrb color="primary" size="xl" position="top-0 right-0" data-id="element-672" />
        <div className="container mx-auto px-6 relative z-10" data-id="element-673">
          <SectionHeader eyebrow="Cosmic Guidance" title="Today's Cosmic Energy" data-id="element-674" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-675">
            <div className="glass p-8 rounded-3xl text-center" data-id="element-676">
              <Moon className="w-12 h-12 text-white mx-auto mb-4 opacity-80" data-id="element-677" />
              <h4 className="font-serif text-xl text-white mb-1" data-id="element-678">
                Waxing Crescent
              </h4>
              <p className="text-gold text-sm uppercase tracking-widest font-bold mb-4" data-id="element-679">
                Moon Phase
              </p>
              <p className="text-beige-muted text-sm" data-id="element-680">
                Perfect time for setting intentions and planting seeds for the
                future.
              </p>
            </div>
            <div className="glass p-8 rounded-3xl text-center border-gold/30 relative overflow-hidden" data-id="element-681">
              <div className="absolute inset-0 bg-gold/5" data-id="element-682"></div>
              <div className="text-4xl text-gold mx-auto mb-4 relative z-10" data-id="element-683">
                ♉
              </div>
              <h4 className="font-serif text-xl text-white mb-1 relative z-10" data-id="element-684">
                Taurus Season
              </h4>
              <p className="text-gold text-sm uppercase tracking-widest font-bold mb-4 relative z-10" data-id="element-685">
                Zodiac
              </p>
              <p className="text-beige-muted text-sm relative z-10" data-id="element-686">
                Focus on grounding, stability, and sensual pleasures.
              </p>
            </div>
            <div className="glass p-8 rounded-3xl text-center" data-id="element-687">
              <Sparkles className="w-12 h-12 text-primary-light mx-auto mb-4" data-id="element-688" />
              <h4 className="font-serif text-xl text-white mb-1" data-id="element-689">
                High Vibration
              </h4>
              <p className="text-gold text-sm uppercase tracking-widest font-bold mb-4" data-id="element-690">
                Energy Level
              </p>
              <p className="text-beige-muted text-sm" data-id="element-691">
                Manifestation power is strong today. Trust your intuition.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Slider */}
      <section className="py-32 relative" data-id="element-692">
        <div className="container mx-auto px-6" data-id="element-693">
          <SectionHeader eyebrow="Client Experiences" title="Transformation Stories" data-id="element-694" />
          <TestimonialsSlider data-id="element-695" />
          <div className="text-center mt-16" data-id="element-696">
            <Button href="/testimonials" variant="ghost" icon={<ArrowRight className="w-4 h-4" data-id="element-698" />} data-id="element-697">
              Read More Stories
            </Button>
          </div>
        </div>
      </section>

      {/* Blog Preview */}
      <section className="py-32 bg-midnight-panel relative" data-id="element-699">
        <div className="container mx-auto px-6" data-id="element-700">
          <SectionHeader eyebrow="Sacred Practices" title="Healing Rituals & Guides" data-id="element-701" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-702">
            {blogPosts.slice(0, 3).map((post, i) => <motion.article key={post.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="group cursor-pointer" data-id="element-703">
                <Link to={`/blog/${post.title.toLowerCase().replace(/\s+/g, '-')}`} data-id="element-704">
                  <div className="aspect-[4/3] rounded-3xl bg-midnight-elevated border border-white/5 mb-6 overflow-hidden relative" data-id="element-705">
                    <div className="absolute inset-0 bg-cover bg-center group-hover:scale-105 transition-transform duration-700" style={{
                  backgroundImage: `url(${blogImageMap[post.title] || images.cosmicAbstract})`
                }} data-id="element-706"></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-midnight via-midnight/40 to-transparent" data-id="element-707"></div>
                    <div className="absolute top-4 left-4 bg-midnight/80 backdrop-blur-md px-3 py-1 rounded-full text-xs font-bold text-gold uppercase tracking-wider border border-white/10" data-id="element-708">
                      {post.category}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-beige-muted mb-3 font-medium uppercase tracking-wider" data-id="element-709">
                    <span data-id="element-710">{post.date}</span>
                    <span className="w-1 h-1 rounded-full bg-gold" data-id="element-711"></span>
                    <span data-id="element-712">{post.readTime}</span>
                  </div>
                  <h3 className="text-2xl font-serif text-white mb-3 group-hover:text-gold transition-colors" data-id="element-713">
                    {post.title}
                  </h3>
                  <p className="text-beige-muted text-sm line-clamp-2" data-id="element-714">
                    {post.excerpt}
                  </p>
                </Link>
              </motion.article>)}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-32 relative" data-id="element-715">
        <div className="container mx-auto px-6 max-w-3xl" data-id="element-716">
          <SectionHeader eyebrow="Common Questions" title="Frequently Asked Questions" data-id="element-717" />
          <FAQAccordion items={faqs.slice(0, 4)} data-id="element-718" />
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 relative overflow-hidden" data-id="element-719">
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: `url(${images.sunsetMountains})`
      }} data-id="element-720"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-midnight via-midnight/80 to-midnight" data-id="element-721"></div>
        <GlowOrb color="gold" size="xl" position="top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-722" />
        <div className="absolute inset-0 bg-midnight/40 backdrop-blur-[2px]" data-id="element-723"></div>

        <div className="container mx-auto px-6 relative z-10 text-center" data-id="element-724">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="max-w-4xl mx-auto glass p-12 md:p-20 rounded-[3rem] border-gold/20" data-id="element-725">
            <span className="text-gold font-serif italic text-2xl mb-6 block" data-id="element-726">
              "Energy flows where intention goes"
            </span>
            <h2 className="text-4xl md:text-6xl font-serif text-white mb-8" data-id="element-727">
              Your Healing Journey Begins Today
            </h2>
            <p className="text-beige-muted mb-12 text-lg max-w-2xl mx-auto" data-id="element-728">
              Reconnect with your inner peace and unlock the life you deserve.
              Book your first healing session today and step into your power.
            </p>
            <Button href="/booking" size="lg" variant="primary" data-id="element-729">
              Book Your Session
            </Button>
          </motion.div>
        </div>
      </section>
    </div>;
}